import { useEffect, useState } from "react";
import {
  Button,
  CheckBox,
  ChevronDownIcon,
  ChevronUpIcon
} from "@dealer-center/core-components";
import styled from "styled-components";
import { Locale } from "@dealer-center/ddc-core-utils";

export interface MultiSelectEntry {
  label: string;
  value: string;
  active: boolean;
}

const StyledMask = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
`;

const StyledDropdown = styled.div`
  display: inline-block;
  position: relative;
`;

const StyledDropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: absolute;
  background-color: white;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
  max-height: 350px;
  padding: 8px 16px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
  width: 100%;
`;

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow: auto;
  li {
    margin: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px;
  Button {
    width: 100%;
  }
`;

export const MultiSelect = ({
  label,
  list,
  realtimeChanges = false,
  onChange
}: {
  label: string;
  list: MultiSelectEntry[];
  realtimeChanges?: boolean;
  onChange: (selected: MultiSelectEntry[]) => void;
}) => {
  const [isShowingModal, setIsShowingModal] = useState(false);
  const [activeFlags, setActiveFlags] = useState<boolean[]>([]);
  const [isAllFlag, setIsAllFlag] = useState(false);

  useEffect(() => {
    if (list) {
      const initialFlags = list.map(item => item.active);
      setActiveFlags(initialFlags);
    }
  }, [list]);

  useEffect(() => {
    if (activeFlags.length > 0) {
      const isSomeActive = activeFlags.some(item => item);
      setIsAllFlag(isSomeActive);
      if (realtimeChanges) {
        handleOnChange();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlags]);

  const handleOnChange = () => {
    if (hasChanged()) {
      const newList = list.map((item, index) => ({
        ...item,
        active: activeFlags[index]
      }));
      onChange(newList);
    }
  };

  const onClose = () => {
    setIsShowingModal(false);
    handleOnChange();
  };

  const hasChanged = () =>
    activeFlags.some((flag, index) => flag !== list[index].active) || false;

  if (activeFlags.length === 0) {
    return null;
  }
  return (
    <StyledDropdown>
      <Button
        analyticsConfiguration={{
          eventName: ""
        }}
        buttonStyle="secondary"
        className="dropbtn"
        endIcon={
          isShowingModal ? (
            <ChevronUpIcon title={""} />
          ) : (
            <ChevronDownIcon title={""} />
          )
        }
        onClick={() => {
          if (isShowingModal) {
            onClose();
          } else {
            setIsShowingModal(!isShowingModal);
          }
        }}
      >
        {label}
      </Button>
      {isShowingModal && (
        <>
          <StyledMask
            onClick={() => {
              onClose();
            }}
          />
          <StyledDropdownContent>
            <StyledHeader>
              <CheckBox
                checked={isAllFlag}
                label={Locale.getText("ALL") as string}
                value={Locale.getText("ALL") as string}
                onChange={() => {
                  const newFlags = [...activeFlags];
                  newFlags.fill(!isAllFlag);
                  setActiveFlags(newFlags);
                }}
              />
            </StyledHeader>
            <StyledList>
              {list.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>
                  <CheckBox
                    checked={activeFlags[index] || false}
                    label={item.label}
                    value={item.value}
                    onChange={() => {
                      const newFlags = [...activeFlags];
                      newFlags[index] = !newFlags[index];
                      setActiveFlags(newFlags);
                    }}
                  />
                </li>
              ))}
            </StyledList>
            <StyledFooter>
              <Button
                analyticsConfiguration={{
                  eventName: "",
                  eventProperties: undefined
                }}
                buttonStyle="primary"
                onClick={onClose}
              >
                {Locale.getText("CLOSE") as string}
              </Button>
            </StyledFooter>
          </StyledDropdownContent>
        </>
      )}
    </StyledDropdown>
  );
};
